<template>
  <div class="mirrors-tab">
    <div class="mirrors-tab__content">
      <v-sheet outlined rounded class="d-flex align-center pa-3 mt-2">
        <v-icon class="mr-4" color="primary">mdi-server-network</v-icon>

        <div class="d-flex align-start">
          <div class="mr-6">
            <div class="caption text-uppercase grey--text">Main Domain</div>
            <div v-if="!loading" class="body-2 font-weight-medium">{{ mainDomainData.name }}</div>
          </div>
          <div>
            <div class="caption text-uppercase grey--text">NS Status</div>
            <v-chip
              v-if="!loading"
              small
              outlined
              :color="mainDomainData.status === 'active' ? 'success' : 'grey'"
              class="pa-2 text-capitalize"
            >
              {{ mainDomainData.status }}
            </v-chip>
          </div>

        </div>

        <v-spacer />

        <template v-if="canEdit && selected.length">
          <v-btn
            outlined
            small
            color="warning"
            class="mr-4 px-2"
            :loading="disableMirrorLoading"
            :disabled="disableMirrorLoading || delMirrorLoading"
            @click="onDisableSelected"
          >
            <v-icon class="mr-1" small>mdi-close-box-outline</v-icon>
            DISABLE
          </v-btn>
          <v-btn
            outlined
            small
            color="error"
            class="mr-4 px-2"
            :loading="delMirrorLoading"
            :disabled="disableMirrorLoading || delMirrorLoading"
            @click="onDeleteSelected"
          >
            <v-icon class="mr-1" small>mdi-delete-outline</v-icon>
            DELETE
          </v-btn>
        </template>

        <v-btn
          v-if="canEdit"
          small
          color="success"
          class="px-2"
          @click="openMirrorForm(null, { readonly: false })"
        >
          Add Mirror
        </v-btn>
      </v-sheet>

      <v-data-table
        v-model="selected"
        :headers="tableHeaders"
        :items="filteredMirrors"
        :options.sync="tableOptions"
        :loading="loading"
        item-key="mirror_id"
        :show-select="canEdit"
        outlined
        :footer-props="{'items-per-page-options': [5, 25, 50, 100, -1]}"
        :items-per-page="50"
        class="mirrors-tab__table mt-4"
      >
        <template #body.prepend>
          <tr v-if="Object.keys(filters).length > 0" class="grey lighten-3">
            <td v-if="canEdit"></td>
            <td v-for="header in tableHeaders" :key="header.value">
              <v-select
                v-if="filters.hasOwnProperty(header.value) && filters[header.value].type === 'select'"
                v-model="filters[header.value].data"
                :items="statusFilterValues"
                hide-details
                dense
                multiple
                clearable
                class="mt-0"
              />
              <v-text-field
                v-if="filters.hasOwnProperty(header.value) && filters[header.value].type === 'text'"
                v-model="filters[header.value].data"
                hide-details
                dense
                class="mt-0"
              />
            </td>
          </tr>
        </template>

        <template #item.groupName="{ item }">
          <span>{{ getMirrorGroupName(item.group_mirror_id) }}</span>
        </template>

        <template #item.redirector="{ item }">
          <span v-if="mirrorRedirectorValue(item)" class="blue--text">
            {{ mirrorRedirectorValue(item) }}
          </span>
        </template>

        <template #item.mirror="{ item }">
          <div
            v-if="isPrimaryMirror(item)"
            class="d-flex align-center"
          >
            <v-icon small color="orange">mdi-crown</v-icon>
            <span class="orange--text font-weight-medium">{{ item.name }}</span>
          </div>
          <div v-else class="d-flex align-center">
            <span class="font-weight-medium">{{ item.name }}</span>
          </div>
        </template>

        <template #item.defaultRule="{ item }">
          <span class="text-capitalize">{{ getGeo(item).rule }}</span>
        </template>

        <template #item.redirectGeo="{ item }">
          <div
            v-if="getGeo(item).redirect.length"
            class="mirrors-tab__geo-cell"
            @click.stop="openGeoListDialog({ title: 'Redirector Geo', countries: getGeo(item).redirect })"
          >
            <lang-row
              :countries="getGeo(item).redirect"
              type="outline"
              color="grey"
              :max-items="3"
              class="lang-row auto-width mirrors-tab__geo-cell-items"
            />
          </div>
        </template>

        <template #item.allowGeo="{ item }">
          <div
            v-if="getGeo(item).allowedDisplay.length"
            class="mirrors-tab__geo-cell"
            @click.stop="openGeoListDialog({ title: 'Allow Geo', countries: getGeo(item).allowedDisplay })"
          >
            <lang-row
              :countries="getGeo(item).allowedDisplay"
              type="outline"
              color="success"
              :max-items="3"
              class="lang-row auto-width mirrors-tab__geo-cell-items"
            />
          </div>
        </template>

        <template #item.blockedGeo="{ item }">
          <div
            v-if="getGeo(item).blockedDisplay.length"
            class="mirrors-tab__geo-cell"
            @click.stop="openGeoListDialog({ title: 'Blocked Geo', countries: getGeo(item).blockedDisplay, variant: 'blocked' })"
          >
            <lang-row
              :countries="getGeo(item).blockedDisplay"
              type="outline"
              color="error"
              :max-items="3"
              class="lang-row auto-width mirrors-tab__geo-cell-items"
            />
          </div>
        </template>

        <template #item.targetGeo="{ item }">
          <div
            v-if="targetGeoCountries(item).length"
            class="mirrors-tab__geo-cell"
          >
            <lang-row
              :countries="targetGeoCountries(item)"
              type="outline"
              color="grey"
              :max-items="3"
              class="lang-row auto-width"
            />
          </div>
        </template>

        <template #item.locales="{ item }">
          <div
            v-if="mirrorLocales(item).length"
            class="mirrors-tab__geo-cell"
            @click.stop="openLocaleListDialog(item)"
          >
            <lang-row
              :countries="mirrorLocales(item)"
              type="outline"
              color="success"
              :max-items="3"
              :fill-first="true"
              class="lang-row auto-width mirrors-tab__geo-cell-items"
            />
          </div>
        </template>

        <template #item.status="{ item }">
          <v-chip
            outlined
            :color="statusColor(item.status)"
            class="text-capitalize mirrors-tab__pill-chip-lg"
          >
            {{ item.status }}
          </v-chip>
        </template>

        <template #item.comments="{ item }">
          <div
            v-if="item?.settings?.comment"
            class="mirrors-tab__geo-cell d-inline-flex align-center justify-center"
            @click.stop="openCommentViewDialog(item)"
          >
            <v-icon small color="green">mdi-comment</v-icon>
          </div>
        </template>

        <template #item.features="{ item }">
          <div
            v-if="(item?.feature_config?.tags || []).length"
            class="mirrors-tab__geo-cell d-flex align-center justify-center"
            style="gap: 4px"
            @click.stop="openFeaturesViewDialog(item)"
          >
            <v-chip
              outlined
              color="warning"
              class="text-capitalize mirrors-tab__geo-cell-items mirrors-tab__pill-chip-lg"
            >
              {{ (item?.feature_config?.tags || [])[0] }}
            </v-chip>
            <div
              v-if="(item?.feature_config?.tags || []).length > 1"
              class="mirrors-tab__length"
            >
              +{{ (item?.feature_config?.tags || []).length - 1 }}
            </div>
          </div>
        </template>

      <template #item.actions="{ item }">
        <v-menu offset-y left>
          <template #activator="{ on }">
            <v-btn icon small v-on="on">
              <v-icon small>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>

          <v-list dense class="mirrors-tab__actions-menu py-1">
            <v-list-item @click="openMirrorForm(item, { readonly: true })">
              <v-list-item-icon><v-icon small>mdi-eye-outline</v-icon></v-list-item-icon>
              <v-list-item-content><v-list-item-title>Overview</v-list-item-title></v-list-item-content>
            </v-list-item>

            <v-list-item v-if="canEdit" @click="openMirrorForm(item, { readonly: false })">
              <v-list-item-icon><v-icon small>mdi-pencil-outline</v-icon></v-list-item-icon>
              <v-list-item-content><v-list-item-title>Edit mode</v-list-item-title></v-list-item-content>
            </v-list-item>

            <v-list-item @click="$emit('show-audit-log', { mirrorId: item.mirror_id })">
              <v-list-item-icon><v-icon small>mdi-history</v-icon></v-list-item-icon>
              <v-list-item-content><v-list-item-title>Audit Log</v-list-item-title></v-list-item-content>
            </v-list-item>

            <v-list-item v-if="canEdit" @click="openCommentForm(item)">
              <v-list-item-icon><v-icon small>mdi-comment-outline</v-icon></v-list-item-icon>
              <v-list-item-content><v-list-item-title>Add Comment</v-list-item-title></v-list-item-content>
            </v-list-item>

            <v-list-item v-if="canEdit" @click="openMirrorForm(item, { readonly: false, duplicate: true })">
              <v-list-item-icon><v-icon small>mdi-content-copy</v-icon></v-list-item-icon>
              <v-list-item-content><v-list-item-title>Duplicate Mirror</v-list-item-title></v-list-item-content>
            </v-list-item>

            <v-list-item v-if="canEdit" @click="openDisableDialog(item)">
              <v-list-item-icon><v-icon small>mdi-close-box-outline</v-icon></v-list-item-icon>
              <v-list-item-content><v-list-item-title>Disable</v-list-item-title></v-list-item-content>
            </v-list-item>

            <v-list-item v-if="canEdit" @click="openDeleteDialog(item)">
              <v-list-item-icon><v-icon small>mdi-delete-outline</v-icon></v-list-item-icon>
              <v-list-item-content><v-list-item-title>Delete</v-list-item-title></v-list-item-content>
            </v-list-item>
          </v-list>
        </v-menu>
        </template>
      </v-data-table>

      <drawer-dialog v-model="mirrorFormOpen" width="70%">
        <mirror-form
          v-if="mirrorFormOpen"
          :mirror="selectedMirror"
          :mirrors="mirrors"
          :groups="groups"
          :redirectors="redirectors"
          :project="project"
          :project_config="project_config"
          :readonly="mirrorFormReadonly"
          :is-duplicate="mirrorFormDuplicate"
          :is-saving="mirrorSaveLoading"
          :can-read-feature-config="canReadFeatureConfig"
          @save="saveMirror"
          @close="closeMirrorForm"
        />
      </drawer-dialog>

      <drawer-dialog v-model="commentFormOpen" width="70%">
        <mirror-comment-form
          v-if="commentFormOpen"
          :mirror="commentTargetMirror"
          :is-loading="mirrorSaveLoading"
          @save="saveMirror"
          @close="closeCommentForm"
        />
      </drawer-dialog>

      <mirror-geo-list-dialog
        :value="geoListDialog"
        :title="geoListTitle"
        :countries="geoListCountries"
        :list-kind="geoListKind"
        :default-locale="geoListDefaultLocale"
        @input="onGeoListDialogInput"
      />

      <v-dialog
        :value="commentViewDialog"
        width="560"
        @input="onCommentViewDialogInput"
      >
        <v-card>
          <v-card-title class="body-1" primary-title>
            Comment
            <v-spacer />
            <v-btn icon @click="closeCommentViewDialog">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="pb-6">
            <v-textarea
              :value="commentViewBody"
              readonly
              outlined
              hide-details
              auto-grow
              rows="6"
              class="mt-0"
            />
          </v-card-text>
        </v-card>
      </v-dialog>

      <v-dialog
        :value="featuresViewDialog"
        width="560"
        @input="onFeaturesViewDialogInput"
      >
        <v-card>
          <v-card-title class="body-1" primary-title>
            Customized features
            <v-spacer />
            <v-btn icon @click="closeFeaturesViewDialog">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="pb-6">
            <div class="d-flex flex-wrap align-center" style="gap: 8px">
              <v-chip
                v-for="tag in featuresViewTags"
                :key="tag"
                outlined
                color="warning"
                class="text-capitalize mirrors-tab__pill-chip"
              >
                {{ tagLabel(tag) }}
              </v-chip>
              <v-btn
                v-if="canReadFeatureConfig"
                icon
                small
                color="success"
                @click.stop="navigateToFeatureConfigForMirror"
              >
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>

      <v-dialog
        v-model="deleteDialog"
        width="560"
      >
        <v-card :loading="delMirrorLoading">
          <v-card-title class="body-1" primary-title>
            Delete Mirror
            <v-spacer />
            <v-btn icon :disabled="delMirrorLoading" @click="closeDeleteDialog">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-divider />

          <v-card-text class="py-6">
            <span class="body-1 grey--text text--darken-4">
              Mirror
              <span class="black--text font-weight-medium">{{ deleteTarget?.name || '-' }}</span>
              will be deleting
            </span>
          </v-card-text>

          <v-divider />

          <v-card-actions>
            <v-spacer />
            <v-btn
              :disabled="delMirrorLoading"
              text
              @click="closeDeleteDialog"
            >
              CANCEL
            </v-btn>
            <v-btn
              outlined
              :loading="delMirrorLoading"
              :disabled="delMirrorLoading"
              color="error"
              @click="confirmDeleteDialog"
            >
              DELETE
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog
        v-model="disableDialog"
        width="560"
      >
        <v-card :loading="disableMirrorLoading">
          <v-card-title class="body-1" primary-title>
            Disable Mirror
            <v-spacer />
            <v-btn icon :disabled="disableMirrorLoading" @click="closeDisableDialog">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-divider />

          <v-card-text class="py-6">
            <span class="body-1 grey--text text--darken-4">
              Mirror
              <span class="black--text font-weight-medium">{{ disableTarget?.name || '-' }}</span>
              will disable
            </span>
          </v-card-text>

          <v-divider />

          <v-card-actions>
            <v-spacer />
            <v-btn
              :disabled="disableMirrorLoading"
              text
              @click="closeDisableDialog"
            >
              CANCEL
            </v-btn>
            <v-btn
              outlined
              :loading="disableMirrorLoading"
              :disabled="disableMirrorLoading"
              color="warning"
              @click="confirmDisableDialog"
            >
              DISABLE
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>

    <v-sheet
      v-if="hasProject && !loading"
      outlined
      rounded
      class="mirrors-tab__footer mt-4 pa-3"
    >
      <div class="d-flex align-start">
        <span class="caption font-weight-medium mr-4">Status info</span>
        <div class="mirrors-tab__footer-status-list">
          <div
            v-for="item in statusInfoItems"
            :key="item.key"
            class="mirrors-tab__footer-status"
          >
            <v-chip
              outlined
              :color="statusColor(item.key)"
              class="mirrors-tab__footer-status-chip mirrors-tab__pill-chip"
            >
              {{ item.title }}
            </v-chip>
            <span class="caption">{{ item.description }}</span>
          </div>
        </div>
      </div>
    </v-sheet>
  </div>
</template>

<script>
  import { API } from '@/utils/api'
  import LangRow from '@/components/core/SelectLang/LangRow/LangRow.vue'
  import { DrawerDialog } from '@/components/core/DrawerDialog'
  import mirrorMixin from '@/modules/mirrors-redirectors/mixins/mirror'
  import mirrorDialogsMixin from '@/modules/mirrors-redirectors/mixins/mirrorDialogs'
  import mirrorActionsMixin from '@/modules/mirrors-redirectors/mixins/mirrorActions'
  import MirrorForm from './MirrorForm.vue'
  import MirrorCommentForm from './MirrorCommentForm.vue'
  import MirrorGeoListDialog from './MirrorGeoListDialog.vue'
  import { TAG_LABELS } from '@/modules/feature-config/constants/const'

  const STATUS_INFO_ITEMS = [
    { key: 'new', title: 'New', description: 'Mng added the domain' },
    { key: 'ns_ready', title: 'NS Ready', description: 'System pulled up NS records' },
    { key: 'password_protected', title: 'Pass. protected', description: 'Domain is configurated and accessible with password' },
    { key: 'active', title: 'Active', description: 'The domain has been released to the world' },
    { key: 'error', title: 'Error', description: 'Error occured' },
    { key: 'deleted', title: 'Deleted', description: 'Mirror was removed' },
    { key: 'draft', title: 'Draft', description: 'In Progress, not active' },
    { key: 'maintenance', title: 'Maintenance', description: 'Maintenance mode' },
  ]

  export default {
    name: 'MirrorsTab',
    emits: ['show-audit-log'],
    mixins: [mirrorMixin, mirrorDialogsMixin, mirrorActionsMixin],
    components: { LangRow, DrawerDialog, MirrorForm, MirrorCommentForm, MirrorGeoListDialog },
    props: {
      project: {
        type: Number,
        default: null,
      },
      searchArgs: {
        type: Object,
        default: null,
      },
      canEdit: {
        type: Boolean,
        default: false,
      },
      canReadFeatureConfig: {
        type: Boolean,
        default: false,
      },
    },
    data: () => ({
      selected: [],
      loading: false,
      mainDomainData: null,
      mirrors: [],
      groups: [],
      redirectors: [],
      project_config: null,

      mirrorSaveLoading: false,
      tagLabels: TAG_LABELS,

      statusFilterValues: STATUS_INFO_ITEMS.map((item) => item.key),

      filters: {
        redirector: { data: '', type: 'text' },
        mirror: { data: '', type: 'text' },
        status: { data: [], type: 'select' },
      },
      tableOptions: {
        page: 1,
        itemsPerPage: 50,
      },

      loadedId: null,

      tableHeaders: [
        { text: 'Mirror Group', value: 'groupName', sortable: false },
        { text: 'Redirector', value: 'redirector', sortable: false },
        { text: 'Mirror', value: 'mirror', sortable: true },
        { text: 'Default Geo Rule', value: 'defaultRule', sortable: false, align: 'center' },
        { text: 'Redirector Geo', value: 'redirectGeo', sortable: false, align: 'center' },
        { text: 'Allow Geo', value: 'allowGeo', sortable: false, align: 'center' },
        { text: 'Blocked Geo', value: 'blockedGeo', sortable: false, align: 'center' },
        { text: 'Banned Geo', value: 'bannedGeo', sortable: false, align: 'center' },
        { text: 'Target Geo', value: 'targetGeo', sortable: false, align: 'center' },
        { text: 'Popular Geo', value: 'popularGeo', sortable: false, align: 'center' },
        { text: 'Language Locale', value: 'locales', sortable: false, align: 'center' },
        { text: 'Status', value: 'status', sortable: false, align: 'center' },
        { text: 'Password', value: 'password', sortable: false, align: 'center' },
        { text: 'Comments', value: 'comments', sortable: false, align: 'center' },
        { text: 'Features', value: 'features', sortable: false, align: 'center' },
        { text: '', value: 'actions', sortable: false, align: 'end' },
      ],
    }),
    computed: {
      hasProject() {
        return this.project != null
      },
      statusInfoItems() {
        return STATUS_INFO_ITEMS
      },
      filteredMirrors() {
        const redirector = String(this.filters.redirector.data ?? '').trim().toLowerCase()
        const mirror     = String(this.filters.mirror.data ?? '').trim().toLowerCase()
        const raw        = this.filters.status.data
        const statuses   = Array.isArray(raw) ? raw : (raw ? [raw] : [])

        return this.mirrors.filter(item => {
          const redirectorMatch = !redirector || this.mirrorRedirectorValue(item).toLowerCase().includes(redirector)
          const mirrorMatch     = !mirror     || String(item?.name ?? '').toLowerCase().includes(mirror)
          const statusMatch     = !statuses.length || statuses.includes(String(item?.status ?? ''))

          return redirectorMatch && mirrorMatch && statusMatch
        })
      },
    },
    watch: {
      project: {
        immediate: true,
        handler(entityId) {
          if (entityId) {
            if (entityId === this.loadedId) {
              return
            }
            this.tableOptions.page = 1
            this.loadMirrors(entityId)
            return
          }
          this.loadedId = null
          this.mainDomainData = null
          this.mirrors = []
          this.groups = []
          this.redirectors = []
          this.project_config = null
        },
      },
      tableOptions: {
        deep: true,
        handler(after, before) {
          if (!this.hasProject || this.loading) return
          if (
            after.itemsPerPage !== before.itemsPerPage ||
            after.page !== before.page
          ) {
            this.loadMirrors(this.project)
          }
        },
      },
    },
    methods: {
      fmtPagination() {
        return {
          items_per_page: this.tableOptions.itemsPerPage,
          page: this.tableOptions.page,
        }
      },
      mirrorRedirectorValue({ group_mirror_id } = {}) {
        return this.redirectors.find((r) => r.group_mirror_id === group_mirror_id)?.name ?? ''
      },
      getMirrorGroupName(groupId) {
        return this.groups.find((g) => g.group_mirror_id === groupId)?.name || '-'
      },

      isPrimaryMirror(item) {
        const group = this.groups.find((g) => g.group_mirror_id === item.group_mirror_id)
        return group?.primary_mirror_id === item.mirror_id
      },

      tagLabel(tag) {
        return this.tagLabels[tag] || tag
      },

      async confirmDeleteDialog() {
        if (!this.deleteTarget?.mirror_id) return
        await this.deleteMirrors([this.deleteTarget.mirror_id])
      },
      async confirmDisableDialog() {
        if (!this.disableTarget?.mirror_id) return
        await this.disableMirrors([this.disableTarget.mirror_id])
      },

      getSelectedMirrorIds() {
        return this.selected.map((item) => item?.mirror_id).filter(Boolean)
      },

      async onBulkAction(action, label) {
        const ids = this.getSelectedMirrorIds()
        if (!ids.length) return
        const yes = await this.$root.$confirm('Confirm', `${label} ${ids.length} selected mirrors?`)
        if (yes) await action(ids)
      },
      onDeleteSelected() {
        return this.onBulkAction(this.deleteMirrors, 'Delete')
      },
      onDisableSelected() {
        return this.onBulkAction(this.disableMirrors, 'Disable')
      },

      async saveMirror({ mirror }) {
        if (!mirror || !this.canEdit) return

        try {
          this.mirrorSaveLoading = true
          await API.call('/$v/mirrors-redirectors/mirrors/update.json', { mirror })
          this.$showSuccess('Saved successfully')
          await this.loadMirrors(this.project)
          this.closeMirrorForm(true)
          this.closeCommentForm(true)
        } catch (e) {
          this.$showError(e?.response?.data?.message || 'Failed to save mirror')
        } finally {
          this.mirrorSaveLoading = false
        }
      },

      async loadMirrors(entityId) {
        this.loading = true
        try {
          const { project: searchProject, ...searchPayload } = this.searchArgs || {}
          const entity_id = searchProject ?? entityId ?? this.project

          const { data = {} } = await API.call('/$v/mirrors-redirectors/mirrors/list.json', {
            entity_id,
            ...searchPayload,
            options: this.fmtPagination(),
          })
          this.loadedId = entity_id
          this.mainDomainData = data.main_domain    ?? null
          this.mirrors        = data.mirrors         ?? []
          this.groups         = data.groups          ?? []
          this.redirectors    = data.redirectors     ?? []
          this.project_config = data.project_config  ?? null
          this.selected = []
        } catch (e) {
          this.$showError(e?.response?.data?.message || 'Failed to load mirrors')
        } finally {
          this.loading = false
        }
      },
    },
  }
</script>

<style lang="scss" scoped>
  .mirrors-tab {
    min-height: 100vh;
    display: flex;
    flex-direction: column;

    &__content {
      flex-grow: 1;
    }

    &__table {
      border: 1px solid #E0E0E0;
    }

    &__pill-chip {
      height: 24px;
      min-height: 24px;
      padding: 5px 8px;
      border-radius: 50px;
      font-size: 12px;
    }

    &__pill-chip-lg {
      height: 32px;
      min-height: 32px;
      padding: 5px 12px;
      border-radius: 50px;
      font-size: 13px;
    }

    &__length {
      background: #828282;
      color: white;
      width: 18px;
      height: 18px;
      border-radius: 50%;
      font-size: 8px;
      line-height: 18px;
      text-align: center;
    }

    &__footer {
      flex-shrink: 0;
      border-color: #E0E0E0;

      &-status-list {
        display: flex;
        flex-wrap: wrap;
        gap: 10px 18px;
      }

      &-status {
        display: flex;
        align-items: center;
        gap: 6px;

        &-chip {
          line-height: 1;
        }
      }
    }

    &__bulk-actions {
      border-color: #2196F3;
    }

    &__actions-menu {
      min-width: 168px;

      & ::v-deep .v-list-item__title {
        color: #000000DE;
        font-size: 12px;
        font-weight: 400;

      }

      &::v-deep .v-list-item__icon {
        margin-right: 0px !important;
      }

      &::v-deep .v-icon {
        color: #1C1B1F;
      }

    }

    &__geo-cell {
      cursor: pointer;
      display: inline-flex;
      width: 100%;
      justify-content: center;

      &-items ::v-deep span {
        cursor: pointer;
      }
    }
  }

  .auto-width {
    width: auto !important;
  }

  .lang-row {
    justify-content: center;
    align-content: center;
  }
</style>
